// CommercialSection — Onglet Commercial actionnable
// Contrats & Engagement + Devis + DocumentsAides + Statut visite + Commentaires
// Chaque bloc a un bouton Modifier ou une action directe
import { useState, useEffect } from "react";
import {
  FileText, CheckCircle2, XCircle, Edit3, Save, X, Loader2, ChevronDown
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { API, getAuthHeaders } from "./helpers";
import { hasDossierAmo } from "../../../utils/tenantProfile";
import { toast } from "sonner";
import { DocumentsAidesWrapper } from "./DocumentsAides";
import DevisFacturesTab from "../DevisFacturesTab";
import { PrestationAMOBlock } from "./PrestationAMOBlock";

// Valeur sentinelle pour « Aucun mandataire » (Radix Select n'accepte pas value="").
// Mappée vers null/"" à l'écriture → donnees_mpr.mandataire_id reste vide en base.
const MANDATAIRE_NONE = "__none__";

export function CommercialSection({ dossier, dossierId, tenantId, isTEF, abandoned, onUpdate, onNavigate }) {
  const verrouille = dossier.verrouille || abandoned;
  // 🔴🔴 N°648 — LE GATE AMO EST ICI, SUR LE BLOC, ⛔ PLUS SUR LE CONTENEUR.
  // ⚠️ MESURÉ EN PROD : gaté un cran au-dessus (DossierDetail filtrait la
  // section « contrats »), il emportait `contrats-cta-top` — c'est-à-dire
  // ⛔ LA PAGE DEVIS de Mario. Contrat AMO, mandat et mandataire sont des
  // gestes d'AMO ; les DEVIS ⛔ n'en sont pas. `hasDossierAmo()` est la
  // capability DÉCLARÉE (mario_bat ⛔ ne la déclare PAS).
  const amo = hasDossierAmo();

  // ─── Contrats editing ───
  const [editingContrats, setEditingContrats] = useState(false);
  const [savingContrats, setSavingContrats] = useState(false);
  const [contratsForm, setContratsForm] = useState({});

  // ─── Mandataire FK (mockup : sélecteur éditable dans Contrats) ───
  // Source unique = donnees_mpr.mandataire_id ; PUT donnees-mpr PARTIEL
  // ({mandataire_id} seul → dot-notation $set, n'écrase pas les autres champs).
  const [mandataires, setMandataires] = useState([]);
  const [defaultMandataireId, setDefaultMandataireId] = useState(null);
  const [savingMandataire, setSavingMandataire] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Liste scopée au tenant DU DOSSIER (pas de la session) → le FK
        // donnees_mpr.mandataire_id résout toujours (fin des homonymes tef/climatpac
        // non résolus quand un super_admin ouvre un dossier d'un autre tenant).
        const res = await fetch(`${API}/api/platform/mandataires`, { headers: getAuthHeaders(dossier?.tenant_id || tenantId) });
        if (res.ok && !cancelled) {
          const data = await res.json();
          setMandataires(data.mandataires || []);
          setDefaultMandataireId(data.default_id || null);
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [dossier?.tenant_id, tenantId]);
  // V1.5 : AMO/TEF = donnees_mpa (/donnees-mpa) ; non-TEF (futur Installateur) = donnees_mpr.
  const dmEndpoint = isTEF ? "donnees-mpa" : "donnees-mpr";
  const currentMandataireId = (isTEF
    ? (dossier?.donnees_mpa?.mandataire_id ?? dossier?.donnees_mpr?.mandataire_id)
    : dossier?.donnees_mpr?.mandataire_id) || "";
  const saveMandataire = async (id) => {
    setSavingMandataire(true);
    try {
      const res = await fetch(`${API}/api/metier/dossiers/${dossierId}/${dmEndpoint}`, {
        method: "PUT",
        headers: getAuthHeaders(dossier?.tenant_id || tenantId, "application/json"),
        credentials: "include",
        body: JSON.stringify({ mandataire_id: id || null }),
      });
      if (res.ok) { toast.success("Mandataire mis à jour"); onUpdate?.(); }
      else toast.error("Erreur mandataire");
    } catch { toast.error("Erreur réseau"); }
    finally { setSavingMandataire(false); }
  };

  const startEditContrats = () => {
    setContratsForm({
      contrat_signe: dossier.contrat_signe || false,
      mandat_signe: dossier.mandat_signe || false,
      mandataire: dossier.mandataire || "",
    });
    setEditingContrats(true);
  };

  const saveContrats = async () => {
    setSavingContrats(true);
    try {
      const headers = getAuthHeaders(tenantId, "application/json");
      const res = await fetch(`${API}/api/metier/dossiers/${dossier.id}`, {
        method: "PATCH",
        headers,
        credentials: "include",
        body: JSON.stringify({
          // P1.2 — mandataire n'est plus édité en texte libre ici : la source unique
          // est le FK donnees_mpr.mandataire_id (sélecteur dans « Données MPA »).
          contrat_signe: contratsForm.contrat_signe,
          mandat_signe: contratsForm.mandat_signe,
        }),
      });
      if (res.ok) {
        toast.success("Contrats mis à jour");
        setEditingContrats(false);
        onUpdate?.();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Erreur sauvegarde");
      }
    } catch {
      toast.error("Erreur réseau");
    } finally {
      setSavingContrats(false);
    }
  };

  return (
    <>
      {/* 1. Contrats & Engagement client */}
      <div data-subnav-id="contrats">
        <div className="rounded-lg overflow-hidden" style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}>
          <details>
            <summary className="flex items-center justify-between gap-2 px-4 py-3 cursor-pointer select-none">
              <span className="text-sm font-medium flex items-center gap-2 min-w-0" style={{ color: "var(--oh-text)" }}>
                <FileText className="w-4 h-4 flex-shrink-0" style={{ color: "var(--oh-hub-commercial)" }} />
                Contrats & Engagement client
              </span>
              <span className="flex items-center gap-2 flex-shrink-0">
                {/* P1.2 — indicateur d'état (visible accordéon replié) : contrat + mandat signés */}
                {amo && (() => {
                  const signed = [dossier.contrat_signe, dossier.mandat_signe].filter(Boolean).length;
                  return (
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap" data-testid="badge-contrats"
                      style={signed === 2
                        ? { background: "var(--oh-stat-green-bg)", color: "var(--oh-stat-green-text)", border: "1px solid var(--oh-stat-green-border)" }
                        : { background: "var(--oh-bg-surface)", color: "var(--oh-text-muted)", border: "1px solid var(--oh-border)" }}>
                      {signed}/2 signé{signed > 1 ? "s" : ""}
                    </span>
                  );
                })()}
                <ChevronDown className="w-4 h-4" style={{ color: "var(--oh-text-muted)" }} />
              </span>
            </summary>
            <div className="px-4 pb-4 space-y-4">
          {/* OMV/CEE : CTA « Générer Devis + Contrat AMO » en tête (parcours devis OMV).
              TEF : la génération vit désormais dans DocumentsZonesTEF (entrée « Documents »
              ci-dessous, « Générer » seul) → on retire ce doublon côté TEF uniquement.
              Refonte Documents (structure cible validée JJ). */}
          {!isTEF && (
            <div data-testid="contrats-cta-top">
              <DevisFacturesTab dossierId={dossierId} tenantId={tenantId} dossier={dossier} onNavigate={onNavigate} />
            </div>
          )}
          <div className="flex justify-end">
            {amo && !verrouille && (
              editingContrats ? (
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="ghost" onClick={saveContrats} disabled={savingContrats} className="h-7 px-2" aria-label="Enregistrer les contrats" data-testid="save-contrats-btn">
                    {savingContrats ? <Loader2 className="w-3.5 h-3.5 animate-spin text-[var(--oh-success-text)]" /> : <Save className="w-3.5 h-3.5 text-[var(--oh-success-text)]" />}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditingContrats(false)} className="h-7 px-2" aria-label="Annuler la modification des contrats" data-testid="cancel-contrats-btn">
                    <X className="w-3.5 h-3.5 text-[var(--oh-error-text)]" />
                  </Button>
                </div>
              ) : (
                <Button size="sm" variant="ghost" onClick={startEditContrats} className="h-7 px-2" aria-label="Modifier les contrats" data-testid="edit-contrats-btn">
                  <Edit3 className="w-3.5 h-3.5" style={{ color: "var(--oh-text-muted)" }} />
                </Button>
              )
            )}
          </div>

          {/* 🔴 N°648 — LE BLOC AMO LUI-MÊME : contrat AMO, mandat, mandataire.
              C'est CELA que la capability doit cacher — ⛔ pas la carte qui
              l'héberge, ⛔ pas les devis. */}
          {amo && (editingContrats ? (
            <div className="space-y-3" data-testid="bloc-amo-contrats">
              {/* Contrat AMO toggle */}
              <label className="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors" style={{ background: contratsForm.contrat_signe ? "rgba(16,185,129,0.08)" : "var(--oh-bg-surface)", border: `1px solid ${contratsForm.contrat_signe ? "rgba(16,185,129,0.3)" : "var(--oh-border)"}` }}>
                <input type="checkbox" checked={contratsForm.contrat_signe} onChange={(e) => setContratsForm(f => ({ ...f, contrat_signe: e.target.checked }))} className="w-4 h-4 accent-emerald-500" />
                <div>
                  <span className="text-sm font-medium" style={{ color: "var(--oh-text)" }}>Contrat AMO</span>
                  <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>Cocher si signé</p>
                </div>
              </label>
              {/* Mandat toggle */}
              <label className="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors" style={{ background: contratsForm.mandat_signe ? "rgba(16,185,129,0.08)" : "var(--oh-bg-surface)", border: `1px solid ${contratsForm.mandat_signe ? "rgba(16,185,129,0.3)" : "var(--oh-border)"}` }}>
                <input type="checkbox" checked={contratsForm.mandat_signe} onChange={(e) => setContratsForm(f => ({ ...f, mandat_signe: e.target.checked }))} className="w-4 h-4 accent-emerald-500" />
                <div>
                  <span className="text-sm font-medium" style={{ color: "var(--oh-text)" }}>Mandat</span>
                  <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>Cocher si signé</p>
                </div>
              </label>
              {/* Mandataire — source unique = FK donnees_mpr.mandataire_id (sélecteur
                  ci-dessous + « Données MPA »). Champ legacy texte racine ignoré. */}
              <div className="text-xs rounded-lg px-3 py-2" style={{ background: "var(--oh-bg-surface)", border: "1px dashed var(--oh-border)", color: "var(--oh-text-muted)" }}>
                Mandataire : à définir via le sélecteur <strong>Données MPA</strong> (mandataire FK).
              </div>
            </div>
          ) : (
            <div className="space-y-2" data-testid="bloc-amo-contrats">
              <div className="text-sm flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="flex items-center gap-1.5" data-testid="contrat-amo-status">
                  {dossier.contrat_signe ? <CheckCircle2 className="w-4 h-4 text-[var(--oh-success-text)]" /> : <XCircle className="w-4 h-4" style={{ color: "var(--oh-text-muted)" }} />}
                  Contrat AMO <span style={{ color: dossier.contrat_signe ? "#10b981" : "var(--oh-text-muted)" }}>{dossier.contrat_signe ? "signé" : "non signé"}</span>
                </span>
                <span className="flex items-center gap-1.5" data-testid="mandat-status">
                  {dossier.mandat_signe ? <CheckCircle2 className="w-4 h-4 text-[var(--oh-success-text)]" /> : <XCircle className="w-4 h-4" style={{ color: "var(--oh-text-muted)" }} />}
                  Mandat <span style={{ color: dossier.mandat_signe ? "#10b981" : "var(--oh-text-muted)" }}>{dossier.mandat_signe ? "signé" : "non signé"}</span>
                </span>
              </div>
              {/* Mandataire : LIÉ à « Données MPA ». Ce sélecteur et celui de DonneesMPR
                  écrivent le MÊME champ (donnees_mpr.mandataire_id via PUT /donnees-mpr)
                  → une seule valeur, deux points d'accès synchronisés (onUpdate rafraîchit
                  le dossier parent dans les deux sens). Pas un doublon indépendant. */}
              <div className="text-sm flex items-center gap-2 flex-wrap" data-testid="mandataire-card">
                <span style={{ color: "var(--oh-text-muted)" }}>Mandataire :</span>
                {!verrouille ? (
                  // MÊME composant que « Données MPA » : Radix/shadcn Select, qui
                  // dérive l'affichage de `value` à chaque rendu (pas le bug du select
                  // natif contrôlé + options async). Même source (donnees_mpr.mandataire_id
                  // avec repli sur le mandataire par défaut, exactement comme DonneesMPR),
                  // même liste (tenant du dossier), même écriture (PUT /donnees-mpr via
                  // saveMandataire) et même synchro (onUpdate).
                  <Select
                    value={currentMandataireId || MANDATAIRE_NONE}
                    onValueChange={(v) => saveMandataire(v === MANDATAIRE_NONE ? "" : v)}
                    disabled={savingMandataire}
                  >
                    <SelectTrigger className="min-w-[220px] h-9 bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] text-[var(--oh-text)]" data-testid="mandataire-select" aria-label="Mandataire">
                      <SelectValue placeholder="Aucun" />
                    </SelectTrigger>
                    <SelectContent>
                      {/* « Aucun » = choix explicite, persiste vide. JAMAIS de mandataire
                          par défaut imposé (règle métier : sinon Cerfa/Mandat faussés). */}
                      <SelectItem value={MANDATAIRE_NONE}>Aucun</SelectItem>
                      {mandataires.map(m => (
                        <SelectItem key={m.id} value={m.id}>{m.raison_sociale}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <span className="font-medium" style={{ color: "var(--oh-text)" }}>
                    {mandataires.find(m => m.id === currentMandataireId)?.raison_sociale || "Aucun"}
                  </span>
                )}
              </div>
              {isTEF && (
                <p className="text-[11px] mt-1" style={{ color: "var(--oh-text-muted)" }}>
                  Le référentiel des mandataires se gère dans Configuration → Mandataire ANAH.
                </p>
              )}
            </div>
          ))}

        {/* CC-19 — Prestation AMO : déplacée en accordéon dédié ci-dessous (P1.2). */}

        {/* (Devis & Contrat AMO déplacé EN TÊTE de carte ci-dessus — point 2 style 2/3) */}
        {/* Pièces du dossier / Données MPA / Préparer & Signer / Documents générés */}
        <div className="mt-3">
          <DocumentsAidesWrapper dossierId={dossierId} tenantId={tenantId} dossier={dossier} isTEF={isTEF} onNavigate={onNavigate} onUpdate={onUpdate} />
        </div>
            </div>
          </details>
        </div>
      </div>

      {/* 2. Prestation AMO — accordéon DÉDIÉ (P1.2 : sorti de Contrats & Engagement).
          Prestation (catalogue) + montant HT/TTC auto (PrestationAMOBlock inchangé). */}
      <div data-subnav-id="prestation-amo">
        <div className="rounded-lg overflow-hidden" style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}>
          <details>
            <summary className="flex items-center justify-between gap-2 px-4 py-3 cursor-pointer select-none">
              <span className="text-sm font-medium flex items-center gap-2 min-w-0" style={{ color: "var(--oh-text)" }}>
                <FileText className="w-4 h-4 flex-shrink-0" style={{ color: "var(--oh-hub-commercial)" }} />
                Prestation AMO
              </span>
              <span className="flex items-center gap-2 flex-shrink-0">
                {/* Mockup : pastille « HT/TTC auto » (info) quand la prestation est
                    définie ; sinon « À définir » (muted) → état toujours visible (règle 1). */}
                {(() => {
                  const has = !!(dossier.prestation_amo_ref || dossier.prestation_amo_designation || dossier.montant_amo);
                  return (
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap" data-testid="badge-prestation-amo"
                      style={has
                        ? { background: "#fff", color: "var(--oh-stat-blue-text)", border: "1px solid var(--oh-stat-blue-border)" }
                        : { background: "var(--oh-bg-surface)", color: "var(--oh-text-muted)", border: "1px solid var(--oh-border)" }}>
                      {has ? "HT/TTC auto" : "À définir"}
                    </span>
                  );
                })()}
                <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: "var(--oh-text-muted)" }} />
              </span>
            </summary>
            <div className="px-4 pb-4">
              <PrestationAMOBlock
                dossier={dossier}
                dossierId={dossierId}
                tenantId={tenantId}
                verrouille={verrouille}
                onUpdate={onUpdate}
              />
            </div>
          </details>
        </div>
      </div>
      {/* Bloc "Accès DAL Mobile" et son Dialog d'envoi ont été déplacés dans OperationSection.jsx
          sous la carte Visite (composant DALMobileBlock). */}

      {/* #5 (2026-06-21) — bloc Commentaires embarqué RETIRÉ : doublon du bouton
          header « Commentaires » → panneau (DossierDetail, réutilise le même
          composant CommentairesDossier). Accès conservé, zéro perte. */}
    </>
  );
}
